import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

// Consumes ?token=... from the reset email and lets the user pick a new password.
// Auto-redirects to /auth on success so they can sign in with the new password.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  usePageSeo({
    title: "Choose a new password — Dividend Portfolio Planner",
    description: "Set a new password for your Dividend Portfolio Planner account. Reset links expire 60 minutes after they're issued.",
    path: "/reset-password",
    noindex: true,
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // Very light client-side password rules — the backend has the source of truth.
  const invalid = password.length < 8 ? "Password must be at least 8 characters" :
                  password !== confirm ? "Passwords don't match" : "";

  const submit = async (e) => {
    e.preventDefault();
    if (invalid) return;
    setBusy(true); setErr("");
    try {
      await api.post("/auth/password/reset-confirm", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — sign in with the new one");
      setTimeout(() => nav("/auth"), 1600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset failed. Please request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-center mb-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Password reset</div>
        <h1 className="serif text-4xl sm:text-5xl mt-3">Pick a new password<span className="text-secondary">.</span></h1>
      </div>

      <Card data-testid="reset-card">
        <CardContent className="p-8">
          {!token ? (
            <MissingToken />
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-done">
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div className="serif text-2xl text-primary">Password updated</div>
              <p className="text-sm text-muted-foreground">
                Redirecting you to sign in…
              </p>
            </div>
          ) : err ? (
            <div className="text-center space-y-4" data-testid="reset-error">
              <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
                <XCircle className="w-7 h-7" />
              </div>
              <div className="serif text-2xl">Reset failed</div>
              <p className="text-sm text-muted-foreground">{err}</p>
              <Button variant="outline" className="rounded-full" onClick={() => nav("/forgot-password")}>
                Request a new link
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Choose a strong password. It should be at least 8 characters.
              </p>
              <div>
                <Label htmlFor="np">New password</Label>
                <div className="relative">
                  <Input
                    id="np"
                    type={show ? "text" : "password"}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    data-testid="reset-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="reset-show"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="cp">Confirm password</Label>
                <Input
                  id="cp"
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm"
                />
              </div>
              {invalid && (password || confirm) && (
                <div className="text-xs text-destructive" data-testid="reset-validation">{invalid}</div>
              )}
              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={busy || !!invalid || !password}
                data-testid="reset-submit"
              >
                {busy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>
                  : <><KeyRound className="w-4 h-4 mr-2" /> Update password</>}
              </Button>
              <div className="text-center text-xs text-muted-foreground pt-1">
                <Link to="/auth" className="text-primary hover:underline underline-offset-2">Back to sign in</Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MissingToken() {
  return (
    <div className="text-center space-y-3" data-testid="reset-missing-token">
      <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive grid place-items-center mx-auto">
        <XCircle className="w-7 h-7" />
      </div>
      <div className="serif text-2xl">Missing reset token</div>
      <p className="text-sm text-muted-foreground">
        Open the reset link from your email — the token isn&apos;t in this URL.
      </p>
      <Link to="/forgot-password" className="text-primary hover:underline underline-offset-2 text-sm">
        Request a new link
      </Link>
    </div>
  );
}
